import React, { useCallback, useRef, useState } from "react";
import { Paperclip, X, FileText, Image as ImageIcon, Video as VideoIcon, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * AttachmentPicker — paperclip + drag-drop zone + removable chips.
 * Uploads files immediately, keeps only their upload_ids in the parent state.
 *
 * Props:
 *   attachments: {id, filename, kind, size, mime}[]
 *   onChange(attachments): controlled setter
 *   maxCount = 6
 */
const KIND_ICON = { image: ImageIcon, video: VideoIcon };
const INLINE_MAX_MB = 95;

function humanKB(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function AttachmentPicker({ attachments = [], onChange, maxCount = 6, disabled = false }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(0);
  const inputRef = useRef(null);

  const remove = useCallback((id) => {
    onChange(attachments.filter((a) => a.id !== id));
  }, [attachments, onChange]);

  const uploadOne = useCallback(async (file) => {
    if (file.size > INLINE_MAX_MB * 1024 * 1024) {
      toast.error(`${file.name}: files over ${INLINE_MAX_MB}MB need chunked upload (coming soon).`);
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      toast.error(`${file.name}: ${msg}`);
      return null;
    }
  }, []);

  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    if (attachments.length + files.length > maxCount) {
      toast.error(`Max ${maxCount} attachments per prompt.`);
      return;
    }
    setUploading((n) => n + files.length);
    try {
      const results = await Promise.all(files.map(uploadOne));
      const good = results.filter(Boolean);
      if (good.length) {
        onChange([...attachments, ...good]);
        toast.success(`${good.length} file${good.length > 1 ? "s" : ""} attached`);
      }
    } finally {
      setUploading((n) => Math.max(0, n - files.length));
    }
  }, [attachments, maxCount, onChange, uploadOne]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="mt-3" data-testid="attachment-picker">
      {/* Chips row */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="attachment-chips">
          {attachments.map((a) => {
            const Icon = KIND_ICON[a.kind] || FileText;
            return (
              <div
                key={a.id}
                className="flex items-center gap-2 border border-border bg-background/70 backdrop-blur-sm pl-2 pr-1 py-1"
                data-testid={`attachment-chip-${a.id}`}
              >
                <Icon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-xs font-mono truncate max-w-[160px]" title={a.filename}>
                  {a.filename}
                </span>
                <span className="text-[10px] text-muted-foreground font-mono">{humanKB(a.size)}</span>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="ml-1 w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-destructive"
                  aria-label={`Remove ${a.filename}`}
                  data-testid={`attachment-remove-${a.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Drop zone + paperclip */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border border-dashed border-border px-3 py-2 flex items-center justify-between text-xs transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "hover:border-foreground/40"
        }`}
        data-testid="attachment-dropzone"
      >
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="w-3 h-3 text-primary" />
          <span className="font-mono uppercase tracking-widest text-[10px]">
            {uploading > 0 ? "Uploading…" : "Drop images, videos, PDFs, or docs"}
          </span>
          <span className="text-[10px]">·</span>
          <span className="text-[10px]">up to {maxCount} files</span>
        </div>
        <button
          type="button"
          disabled={disabled || uploading > 0 || attachments.length >= maxCount}
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1 border border-border px-2 py-1 hover:bg-foreground hover:text-background transition-colors font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
          data-testid="attachment-picker-btn"
        >
          {uploading > 0 ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
          Attach
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,video/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/*"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="attachment-file-input"
        />
      </div>
    </div>
  );
}
